import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary } from './CommissionOverview';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const BENEFICIARY_TYPES = ['employee', 'external_user', 'referrer', 'partner', 'consultant', 'company_owner', 'salesperson', 'manager', 'cross_company_user'];
const STATUSES = ['active', 'inactive', 'suspended', 'archived'];
const COMMISSION_SOURCES = [
  { value: 'registered_students', label: 'Registered students' },
  { value: 'manual', label: 'Manual entries' },
];

export default function CommissionBeneficiaries() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);
  const [sourceDraft, setSourceDraft] = useState('');
  const [employees, setEmployees] = useState([]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/commissions/beneficiaries?per_page=50`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    fetch(`${API}/companies/${companyId}/employees`, { headers: getAuthHeaders() })
      .then(res => res.ok ? res.json() : { data: [] })
      .then(json => setEmployees(Array.isArray(json.data) ? json.data : []))
      .catch(() => setEmployees([]));
  }, [companyId]);

  const openDetail = async (id) => {
    if (detail?.beneficiary?.id === id) {
      setDetail(null);
      return;
    }
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/beneficiaries/${id}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      setDetail(json);
      setSourceDraft(json.beneficiary?.commission_source || '');
    } catch (e) { console.error(e); }
  };

  const changeStatus = async (id, newStatus) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/beneficiaries/${id}/status`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed');
      fetchList();
      setDetail(null);
    } catch (e) { console.error(e); }
  };

  const saveCommissionSource = async () => {
    if (!detail || !sourceDraft) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/beneficiaries/${detail.beneficiary.id}`, {
        method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ commission_source: sourceDraft }),
      });
      if (!res.ok) throw new Error('Failed');
      await openDetail(detail.beneficiary.id);
      fetchList();
    } catch (e) { console.error(e); }
  };

  const fmt = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>👤 Commission Beneficiaries</h2>
        <button style={btnPrimary} onClick={() => setShowCreate(true)}>+ New Beneficiary</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, width: 220 }} />
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inputStyle, width: 160 }}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Code', 'Name', 'Type', 'Commission source', 'Department', 'Status', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No beneficiaries yet</td></tr>}
              {list.map(b => <React.Fragment key={b.id}>
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{b.employee?.employee_code || b.beneficiary_code}</td>
                  <td style={tdStyle}>{b.display_name || b.user?.name || '—'}</td>
                  <td style={tdStyle}>{(b.beneficiary_type || '').replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{COMMISSION_SOURCES.find(s => s.value === b.commission_source)?.label || '—'}</td>
                  <td style={tdStyle}>{b.department?.name || b.department_name || '—'}</td>
                  <td style={tdStyle}><Badge value={b.status} /></td>
                  <td style={tdStyle}><button style={btnSecondary} onClick={() => openDetail(b.id)}>{detail?.beneficiary?.id === b.id ? 'Hide details' : 'View details'}</button></td>
                </tr>
                {detail?.beneficiary?.id === b.id && <tr>
                  <td colSpan={7} style={{ ...tdStyle, padding: 0, background: '#f8fafc' }}>
                    <ExpandedDetails detail={detail} sourceDraft={sourceDraft} setSourceDraft={setSourceDraft}
                      saveCommissionSource={saveCommissionSource} changeStatus={changeStatus} fmt={fmt} />
                  </td>
                </tr>}
              </React.Fragment>)}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <CreateBeneficiaryModal companyId={companyId} employees={employees} onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchList(); }} />
      )}

    </div>
  );
}

function ExpandedDetails({ detail, sourceDraft, setSourceDraft, saveCommissionSource, changeStatus, fmt }) {
  const beneficiary = detail.beneficiary || {};
  const registrations = detail.eligible_registrations || [];
  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>{beneficiary.display_name}</div>
          <div style={{ color: '#6b7280', fontSize: 12 }}>{beneficiary.employee?.email || beneficiary.user?.email || 'No email'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Calculate from</span>
          <select value={sourceDraft} onChange={e => setSourceDraft(e.target.value)} style={{ ...inputStyle, width: 190, padding: '6px 8px' }}>
            <option value="">Select a source...</option>
            {COMMISSION_SOURCES.map(source => <option key={source.value} value={source.value}>{source.label}</option>)}
          </select>
          <button style={{ ...btnSecondary, padding: '6px 10px' }} onClick={saveCommissionSource} disabled={!sourceDraft || sourceDraft === beneficiary.commission_source}>Save</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(110px, 1fr))', gap: 12, marginBottom: 18 }}>
        <Info label="Expected" value={fmt(detail.totals?.expected)} />
        <Info label="Earned" value={fmt(detail.totals?.earned)} />
        <Info label="Due" value={fmt(detail.totals?.due)} />
        <Info label="Paid" value={fmt(detail.totals?.paid)} />
      </div>

      {beneficiary.commission_source === 'registered_students' && (
        <div>
          <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>Students registered by {beneficiary.display_name} ({registrations.length})</div>
          {!detail.commission_registrar_linked && <div style={{ marginBottom: 10, color: '#b45309', fontSize: 13 }}>Link this employee to their system account to track their registrations automatically.</div>}
          <div style={{ border: '1px solid #dbe3ee', borderRadius: 8, background: '#fff', overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead><tr>{['Student', 'Cohort', 'Registered on', 'Status'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {registrations.length === 0 && <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No registered students yet</td></tr>}
                {registrations.map(enrollment => <tr key={enrollment.id}>
                  <td style={tdStyle}>{enrollment.student?.full_name || '—'}{enrollment.student?.student_code && <div style={{ fontSize: 11, color: '#6b7280' }}>{enrollment.student.student_code}</div>}</td>
                  <td style={tdStyle}>{enrollment.cohort?.cohort_code || enrollment.cohort?.name || '—'}</td>
                  <td style={tdStyle}>{enrollment.enrollment_date || '—'}</td>
                  <td style={tdStyle}><Badge value={enrollment.status} /></td>
                </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
        {STATUSES.filter(status => status !== beneficiary.status).map(status => (
          <button key={status} style={btnSecondary} onClick={() => changeStatus(beneficiary.id, status)}>Set {status}</button>
        ))}
      </div>
    </div>
  );
}

function CreateBeneficiaryModal({ companyId, employees, onClose, onCreated }) {
  const [form, setForm] = useState({ employee_id: '', beneficiary_type: 'employee', commission_source: '', cross_company_eligible: false, referral_eligible: false });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!form.employee_id) { setErr('Please select an employee'); return; }
    if (!form.commission_source) { setErr('Please select where the commission is calculated from'); return; }
    setSaving(true); setErr('');
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/beneficiaries`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.message || 'Failed to create'); }
      onCreated();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>New Commission Beneficiary</h3>
        {err && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <FormRow label="Employee *">
          <select value={form.employee_id} onChange={e => setForm({ ...form, employee_id: e.target.value })} style={inputStyle}>
            <option value="">Select an employee...</option>
            {employees.map(employee => <option key={employee.id} value={employee.id}>{[employee.first_name, employee.middle_name, employee.last_name].filter(Boolean).join(' ')}</option>)}
          </select>
        </FormRow>
        <FormRow label="Beneficiary Type">
          <select value={form.beneficiary_type} onChange={e => setForm({ ...form, beneficiary_type: e.target.value })} style={inputStyle}>
            {BENEFICIARY_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
        </FormRow>
        <FormRow label="Calculate commission from *">
          <select value={form.commission_source} onChange={e => setForm({ ...form, commission_source: e.target.value })} style={inputStyle}>
            <option value="">Select a source...</option>
            {COMMISSION_SOURCES.map(source => <option key={source.value} value={source.value}>{source.label}</option>)}
          </select>
        </FormRow>
        <FormRow label="Cross-company Eligible">
          <input type="checkbox" checked={form.cross_company_eligible} onChange={e => setForm({ ...form, cross_company_eligible: e.target.checked })} />
        </FormRow>
        <FormRow label="Referral Eligible">
          <input type="checkbox" checked={form.referral_eligible} onChange={e => setForm({ ...form, referral_eligible: e.target.checked })} />
        </FormRow>
        <FormRow label="Notes">
          <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...inputStyle, minHeight: 60 }} />
        </FormRow>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: '#1a1a2e' }}>{value}</div>
    </div>
  );
}

export function FormRow({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

export const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
export const modalStyle = { background: '#fff', borderRadius: 12, padding: 24, width: 520, maxHeight: '85vh', overflowY: 'auto', fontSize: 13 };
